import { graphQlClient } from "lib/graphql-client.js";
import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useContext,
} from "react";
// import Link from "next/link";
import dynamic from "next/dynamic";
import { gql } from "@apollo/client";
// import useOutsideClick from "../../../lib/hooks/useOutsideClick";
import { ProductJsonLd } from "next-seo";
// import { processHubspotForm } from "lib/api/hubspot/form-submit";

import { CurrencyContext } from "../../../contexts/currency";
import { useCart } from "lib/hooks/useCart";

import {
  formatDateYMD,
  addDaysToDate,
} from "../../../utils/helpers/dateTimeHelpers";

import Layout from "components/Layout";
// import HeroSlider from "components/HeroSlider/HeroSlider";
import MainSlider from "@/components/Slider/BlazeSlider/BlazeSlider";
import { generateBlazeSlides } from "utils/sliders";

const BookingInfo = dynamic(() =>
  import("components/ExperiencePage/BookingInfo/BookingInfo")
);
import ExperienceDetails from "components/ExperiencePage/ExperienceDetails/ExperienceDetails";
// const SlideOver = dynamic(() => import("components/ui/SlideOver/SlideOver"));
// import SlideOver from "components/ui/SlideOver/SlideOver";
// import { bookingFormSchema } from "components/forms/BookingForm/bookingFormSchema";
// const FormContainer = dynamic(() =>
//   import("components/forms/FormContainer/FormContainer")
// );
// import FormContainer from "components/forms/FormContainer/FormContainer";
// const BookingForm = dynamic(() =>
//   import("components/forms/BookingForm/BookingForm")
// );
// const HubspotFormEmbed = dynamic(() => import("components/HubSpotForms/HubSpotFormEmbed"))
const ExperienceFooterBar = dynamic(() =>
  import("components/ExperiencePage/ExperienceFooterBar/ExperienceFooterBar")
);

import { fetchLayoutData, fetchLayoutDataServer } from "lib/api/strapi-layout";
import { site } from "config";

import styles from "./Experience.module.scss";
import {
  fetchExperience,
  fetchRelatedExperiences,
} from "lib/api/strapi-experiences";
import { fetchYouTubeData } from "utils/video";

const Experience = ({ experience, relatedExperiences, layoutData }) => {
  const seo = {
    ...experience?.seo,
    canonicalURL: site + "/experience/" + experience?.slug,
  };

  const currencyContext = useContext(CurrencyContext);
  const currency = currencyContext.currency;

  const { addExperienceToCart, cart } = useCart();

  const [bookingDetails, setBookingDetails] = useState({
    title: experience?.title,
    experience_code: experience?.metadata?.experience_code ?? experience?.id,
    peopleCount: 1,
    startDate: formatDateYMD(new Date()),
    endDate: formatDateYMD(addDaysToDate(new Date(), 7)),
    duration: 1,
    // totalPrice: experience.metadata.retail_price,
  });

  const slides = generateBlazeSlides({
    slides: experience?.hero_slider.map((slide) => {
      return { ...slide, id: experience?.strapiId };
    }),
    type: "experienceMinimal",
    ctaButton: false,
  });

  const onBookingInputChange = useCallback(
    (property, input) => {
      /* Handler that can be passed to any input and used to update booking state*/
      /* property: "string", input: any */
      /* use as: onChange={() => onBookingInputChange(property, input)} */

      setBookingDetails((prevBookingDetails) => {
        if (prevBookingDetails[property] == property) {
          return prevBookingDetails;
        }
        return { ...prevBookingDetails, [property]: input };
      });
    },

    [setBookingDetails]
  );

  useMemo(() => {
    const newEndDate = formatDateYMD(
      addDaysToDate(bookingDetails.startDate, bookingDetails.duration * 7)
    );

    setBookingDetails((prevBookingDetails) => ({
      ...prevBookingDetails,
      endDate: newEndDate,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    bookingDetails.startDate,
    bookingDetails.endDate,
    bookingDetails.duration,
    onBookingInputChange,
  ]);

  const [slideOverIsActive, setSlideOverIsActive] = useState(false);
  const [wasSlideOverActivated, setWasSlideOverActivated] = useState(false);
  const [isAddingToCart, setIsAddingToCart] = useState(false);

  const handleBookingButtonClick = async () => {
    setIsAddingToCart(true);
    const addToCartResponse = await addExperienceToCart({
      formData: {
        people: 1,
        startDate: "None",
        duration: 1,
        product_metadata: {
          id: experience?.id,
          slug: experience?.slug,
          currencies: experience?.metadata?.currencies,
        },
      },
    });
    setTimeout(() => {
      setIsAddingToCart(false);
    }, 500);

    return addToCartResponse;
  };

  // const handleBookingButtonClick = () => {
  //   setSlideOverIsActive((prevSlideOverIsActive) => !prevSlideOverIsActive);
  //   setWasSlideOverActivated(true);
  // };
  // const closeBookingForm = () => {
  //   if (slideOverIsActive) {
  //     setSlideOverIsActive(false);
  //   }
  // };

  const buttonRef = useRef();
  const footerButtonRef = useRef();
  // const bookingSlideOverRef = useOutsideClick(closeBookingForm, [
  //   buttonRef,
  //   footerButtonRef,
  // ]);
  // const relatedExperiencesRef = useRef();

  return (
    <Layout seo={seo} showEmailSignup={false} layoutData={layoutData}>
      <ProductJsonLd
        productName={experience?.title}
        images={experience?.hero_slider?.map(
          (slide) => slide?.sliderImageLarge?.url
        )}
        description={experience?.seo?.metaDescription}
        slogan="WhereNext Experiences is a travel company providing impactful experiences across the globe with a focus on volunteering and animal conservation."
        offers={[
          {
            price: experience?.metadata?.currencies?.usd,
            priceCurrency: "USD",
            availability:
              experience?.availability?.status === "Open"
                ? "https://schema.org/InStock"
                : "https://schema.org/OutOfStock",
            url: `${site}/experiences/${experience?.slug}`,
            seller: {
              name: "WhereNext.to",
            },
          },
        ]}
      />
      {experience?.hero_slider && (
        <div
          className="hero-container"
          style={{ width: "unset", overflowX: "hidden" }}
        >
          <MainSlider sideNav={true}>{slides}</MainSlider>
        </div>
      )}
      <div className={`variable-container ${styles.experiencePageContainer}`}>
        <section className={`${styles.experienceDetails}`}>
          <ExperienceDetails
            experience={experience}
            relatedExperiences={relatedExperiences}
            currency={currency}
          />
        </section>
        <div className={`${styles.bookingInfoContainer}`}>
          <BookingInfo
            data={experience}
            inputHandler={onBookingInputChange}
            // onBookingButtonClick={handleBookingButtonClick}
            bookingDetails={bookingDetails}
            wasSlideOverActivated={wasSlideOverActivated}
            buttonRef={buttonRef}
            currency={currency}
          />
        </div>
      </div>
      {/* <SlideOver
        isActive={slideOverIsActive}
        ref={bookingSlideOverRef}
        handleCloseButtonClick={closeBookingForm}
      >
        <h2>More Information</h2>
        <FormContainer
          pageName={seo.metaTitle}
          // form={BookingForm}
          submitHandler={processHubspotForm}
          temporarySubmitHandler={addExperienceToCart}
          formId="28f81907-c390-4adb-a8ce-1a51743ab0b7"
          schema={bookingFormSchema}
        >
          <BookingForm
            productMetadata={{
              id: experience?.metadata?.experience_code,
              supplier_code: experience?.metadata?.supplier_code,
              slug: experience?.slug,
              currencies: experience?.metadata?.currencies,
              type: "experience",
            }}
          />
        </FormContainer>
        <div>
          <p>
            WHERENEXT EXPERIENCES PTE. LTD. needs the contact information you
            provide to us to contact you about our products and services. You
            may unsubscribe from these communications at any time. For
            information on how to unsubscribe, as well as our privacy practices
            and commitment to protecting your privacy, please review our{" "}
            <Link href="/privacy">Privacy Policy</Link>.
          </p>
        </div> */}
      {/* <BookingForm pageName={seo.metaTitle} /> */}
      {/* <HubExperienceBooking bookingDetails={bookingDetails} /> */}
      {/* </SlideOver> */}

      <ExperienceFooterBar
        experience={experience}
        onClick={handleBookingButtonClick}
        buttonRef={footerButtonRef}
        isAddingToCart={isAddingToCart}
        // show={showFooterBar}
      />
    </Layout>
  );
};

export default Experience;

export async function getStaticProps(context) {
  // Fetch experience by slug
  const { slug } = context.params;

  const publicationState =
    process.env.NEXT_PUBLIC_ENV.toLowerCase() === "preview"
      ? "PREVIEW"
      : "LIVE";

  const experience = await fetchExperience(slug, true, publicationState);

  let heroSlider = [];

  if (experience?.hero_slider) {
    for (let slide of experience?.hero_slider) {
      if (!!slide?.video && !!slide?.video?.url) {
        const youTubeData = await fetchYouTubeData({
          url: slide?.video?.url,
        });

        heroSlider.push({
          ...slide,
          video: {
            ...slide?.video,
            youTubeData: youTubeData ?? null,
          },
        });
      } else {
        heroSlider.push(slide);
      }
    }
  }

  experience.hero_slider = heroSlider;

  const country = await experience?.location?.country?.country.toLowerCase();

  const relatedExperiences = await fetchRelatedExperiences({
    country: country,
    exclude: [slug],
    start: 0,
    limit: 5,
    process: true,
  });

  // const layoutData = await fetchLayoutData();
  const layoutData = await fetchLayoutDataServer();

  return {
    props: {
      experience,
      layoutData,
      relatedExperiences,
    },
  };
}

export async function getStaticPaths() {
  const strapiApolloClient = graphQlClient();

  let experienceSlugsResponse = await strapiApolloClient.query({
    query: gql`
      query ExperienceSlugQuery($publicationState: PublicationState!) {
        experiences(
          pagination: { limit: 5000 }
          publicationState: $publicationState
        ) {
          data {
            attributes {
              slug
            }
          }
        }
      }
    `,
    variables: {
      publicationState:
        process.env.NEXT_PUBLIC_ENV.toLowerCase() === "preview"
          ? "PREVIEW"
          : "LIVE",
    },
  });

  const experiencePaths = experienceSlugsResponse.data.experiences.data.map(
    (experience) => {
      return { params: { slug: experience.attributes.slug } };
    }
  );

  return {
    paths: experiencePaths,
    fallback: false,
  };
}
